import { getEntries, appendGroupedOptions } from '@theme/nw-device-data';

/**
 * <device-finder-form> wraps the brand / model / category selects rendered by
 * sections/device-finder.liquid. There is no submit button: picking a value
 * navigates immediately, since a device finder exists to get someone to a
 * collection, not to hold a choice they still have to confirm.
 *
 * Brands and models are read from the store's own brand pages rather than a
 * hardcoded list, so a new device becomes selectable as soon as its page
 * exists. Choosing a category lands on the model's collection narrowed by
 * that category; choosing a model with no categories of its own lands on the
 * model's collection directly.
 */
class DeviceFinderForm extends HTMLElement {
  connectedCallback() {
    this.brand = this.querySelector('[data-device-finder-brand]');
    this.model = this.querySelector('[data-device-finder-model]');
    this.category = this.querySelector('[data-device-finder-category]');
    this.brandIndex = this.closest('[data-brand-index]')?.dataset.brandIndex || '/pages/shop-by-brand';

    this.brand?.addEventListener('change', this.handleBrandChange);
    this.model?.addEventListener('change', this.handleModelChange);
    this.category?.addEventListener('change', this.handleCategoryChange);

    if (this.model) this.model.disabled = true;
    // Only starts disabled if it has nothing but the placeholder - the header
    // drawer's category select ships pre-populated with real category links
    // and should stay usable before a brand or model is ever touched.
    if (this.category && this.category.options.length <= 1) this.category.disabled = true;
    this.loadBrands();
  }

  disconnectedCallback() {
    this.brand?.removeEventListener('change', this.handleBrandChange);
    this.model?.removeEventListener('change', this.handleModelChange);
    this.category?.removeEventListener('change', this.handleCategoryChange);
  }

  async loadBrands() {
    if (!this.brand) return;

    try {
      const brands = await getEntries(this.brandIndex);
      appendGroupedOptions(this.brand, brands);
    } catch {
      // Leave the placeholder in place; the category route below still works.
    }
  }

  handleBrandChange = async () => {
    if (!this.model) return;

    this.resetSelect(this.model, 'Choose a model');
    this.resetSelect(this.category, 'Choose a category');

    if (!this.brand.value) return;

    try {
      const models = await getEntries(this.brand.value);
      appendGroupedOptions(this.model, models);
      this.model.disabled = models.length === 0;
    } catch {
      // Stays disabled; submitting without a model is still handled.
    }
  };

  // A device collection page lists its categories as the same #model-images
  // tiles the brand pages use, so the categories a device actually has come
  // from the device itself rather than a fixed list that goes stale.
  handleModelChange = async () => {
    if (!this.category) {
      if (this.model.value) window.location.href = this.model.value;
      return;
    }

    this.resetSelect(this.category, 'Choose a category');
    if (!this.model.value) return;

    try {
      const categories = await getEntries(this.model.value);
      appendGroupedOptions(this.category, categories);
      this.category.disabled = categories.length === 0;
      // No categories of its own: the model page is as specific as this gets.
      if (categories.length === 0) {
        window.location.href = this.model.value;
        return;
      }

      // On a category collection the bar defaults to that category, so go
      // straight to the device's version of it rather than asking again.
      const current = this.findCurrentCategory(categories);
      if (current) {
        this.category.value = current.url;
        window.location.href = current.url;
      } else if (this.dataset.categoryTag) {
        // This device doesn't carry the page's category; stop the
        // placeholder still naming it.
        this.category.options[0].textContent = '3. Choose a category';
      }
    } catch {
      // Fetch failed rather than came back empty - still land somewhere real.
      window.location.href = this.model.value;
    }
  };

  // Device category links end in the category's tag
  // (/collections/<device>/<tag>, or <tag> joined with '+').
  findCurrentCategory(categories) {
    const tag = this.dataset.categoryTag;
    if (!tag) return null;

    return categories.find((entry) => {
      const path = new URL(entry.url, window.location.origin).pathname;
      return path.split(/[/+]/).includes(tag);
    });
  }

  handleCategoryChange = () => {
    if (this.category.value) window.location.href = this.category.value;
  };

  resetSelect(select, placeholder) {
    if (!select) return;
    const label = select.options[0]?.textContent || placeholder;
    select.innerHTML = '';
    select.append(new Option(label, ''));
    select.disabled = true;
  }
}

if (!customElements.get('device-finder-form')) {
  customElements.define('device-finder-form', DeviceFinderForm);
}
